import { useState, useEffect, useRef } from 'react';
import {
    VideoOff, Camera, CameraOff, Mic, MicOff, Radio, Shield, Signal,
    WifiOff, Eye, AlertTriangle, RefreshCw, Maximize2, Minimize2, X,
    Phone, Monitor, Layers, ChevronRight, Users, Headphones
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import api from '@/services/api';

// ═══════════════════════════════════════════════════
// CONFIG
// ═══════════════════════════════════════════════════
const MEDIA_SERVER = import.meta.env.VITE_MEDIA_SERVER || 'https://vantak.io';

// ICE servers (STUN/TURN autoalojados, soberanía). Las credenciales TURN son
// EFÍMERAS: las firma el backend (/api/v1/webrtc/ice, HMAC compartido con
// coturn use-auth-secret) — nada queda hardcodeado en el bundle (P-12).
// Fallback a solo-STUN si el endpoint no responde.
const STUN_FALLBACK: RTCIceServer[] = [
    { urls: import.meta.env.VITE_STUN_URL || 'stun:stun.l.google.com:19302' },
];

let iceCache: { servers: RTCIceServer[]; expiresAt: number } | null = null;

async function getIceServers(): Promise<RTCIceServer[]> {
    const now = Date.now();
    if (iceCache && now < iceCache.expiresAt) return iceCache.servers;
    try {
        const res = await api.get('/api/v1/webrtc/ice');
        const servers: RTCIceServer[] = res.data?.iceServers?.length ? res.data.iceServers : STUN_FALLBACK;
        // Renovar con margen: la mitad del TTL de la credencial.
        const ttlMs = (res.data?.ttlSeconds ?? 3600) * 1000;
        iceCache = { servers, expiresAt: now + ttlMs / 2 };
        return servers;
    } catch {
        return STUN_FALLBACK;
    }
}

type StreamMode = 'idle' | 'broadcasting' | 'viewing';
type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';
type PageView = 'select' | 'channel' | 'monitor';

interface ActiveOperation {
    id: number;
    code: string;
    description: string;
    type: string;
    status: string;
    opcLevel?: string;
    zone?: string;
    eventType?: string;
}

// Genera URLs basadas en el código de operación
const getStreamUrls = (opCode: string) => {
    const slug = opCode.toLowerCase().replace(/[^a-z0-9]/g, '-');
    return {
        whip: `${MEDIA_SERVER}/live/${slug}/whip`,
        whep: `${MEDIA_SERVER}/live/${slug}/whep`,
        hls: `${MEDIA_SERVER}/stream/live/${slug}/index.m3u8`,
    };
};

const getVoiceUrl = (opCode: string, username: string) => {
    const channel = opCode.replace(/[^a-zA-Z0-9-]/g, '');
    return `${MEDIA_SERVER}/voice/?username=${encodeURIComponent(username)}&channel=${encodeURIComponent(channel)}`;
};

// ═══════════════════════════════════════════════════
// MONITOR CARD — Video + Voice per operation (toggleable)
// ═══════════════════════════════════════════════════
function MonitorCard({ op, colors, voiceUrl, onJoin }: {
    op: ActiveOperation;
    colors: { bg: string; border: string; badge: string; dot: string };
    voiceUrl: string;
    onJoin: () => void;
}) {
    const { t } = useTranslation();
    const videoRef = useRef<HTMLVideoElement>(null);
    const pcRef = useRef<RTCPeerConnection | null>(null);
    const [videoEnabled, setVideoEnabled] = useState(false);
    const [videoStatus, setVideoStatus] = useState<'off' | 'connecting' | 'live' | 'offline'>('off');
    const [voiceEnabled, setVoiceEnabled] = useState(false);

    // Connect/disconnect video on toggle
    useEffect(() => {
        if (!videoEnabled) {
            // Cleanup
            pcRef.current?.close(); pcRef.current = null;
            if (videoRef.current) { videoRef.current.srcObject = null; videoRef.current.src = ''; }
            setVideoStatus('off');
            return;
        }

        let cancelled = false;
        const urls = getStreamUrls(op.code);

        const tryConnect = async () => {
            setVideoStatus('connecting');
            try {
                const pc = new RTCPeerConnection({ iceServers: await getIceServers() });
                pcRef.current = pc;
                pc.addTransceiver('video', { direction: 'recvonly' });
                pc.addTransceiver('audio', { direction: 'recvonly' });
                pc.ontrack = (e) => { if (videoRef.current && e.streams[0]) videoRef.current.srcObject = e.streams[0]; };
                const offer = await pc.createOffer();
                await pc.setLocalDescription(offer);
                await new Promise<void>(r => {
                    if (pc.iceGatheringState === 'complete') r();
                    else { pc.addEventListener('icegatheringstatechange', () => { if (pc.iceGatheringState === 'complete') r(); }); setTimeout(r, 3000); }
                });
                const res = await fetch(urls.whep, { method: 'POST', headers: { 'Content-Type': 'application/sdp' }, body: pc.localDescription!.sdp });
                if (!res.ok) throw new Error('No WHEP');
                await pc.setRemoteDescription({ type: 'answer', sdp: await res.text() });
                await new Promise<void>((resolve, reject) => {
                    const t = setTimeout(() => reject(new Error('timeout')), 6000);
                    pc.onconnectionstatechange = () => {
                        if (pc.connectionState === 'connected') { clearTimeout(t); resolve(); }
                        else if (pc.connectionState === 'failed') { clearTimeout(t); reject(new Error('failed')); }
                    };
                });
                if (!cancelled) setVideoStatus('live');
            } catch {
                if (cancelled) return;
                // HLS fallback
                try {
                    const Hls = (await import('hls.js')).default;
                    if (!videoRef.current || cancelled) return;
                    if (Hls.isSupported()) {
                        const hls = new Hls({ enableWorker: true, lowLatencyMode: true, liveSyncDurationCount: 2, maxBufferLength: 4 });
                        hls.loadSource(urls.hls); hls.attachMedia(videoRef.current);
                        hls.on(Hls.Events.MANIFEST_PARSED, () => { videoRef.current?.play(); if (!cancelled) setVideoStatus('live'); });
                        hls.on(Hls.Events.ERROR, (_: any, d: any) => { if (d.fatal && !cancelled) setVideoStatus('offline'); });
                    }
                } catch { if (!cancelled) setVideoStatus('offline'); }
            }
        };

        tryConnect();
        return () => { cancelled = true; pcRef.current?.close(); pcRef.current = null; };
    }, [videoEnabled, op.code]);

    const toggleVideo = () => setVideoEnabled(v => !v);
    const toggleVoice = () => setVoiceEnabled(v => !v);

    return (
        <div className={`rounded-2xl overflow-hidden border ${colors.border} bg-gray-900/30`}>
            {/* Header */}
            <div className={`flex items-center justify-between px-4 py-2.5 bg-gradient-to-r ${colors.bg} border-b ${colors.border}`}>
                <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${colors.dot} animate-pulse`} />
                    <span className="text-[10px] font-black text-white uppercase tracking-wider">{op.code}</span>
                </div>
                <div className="flex items-center gap-2">
                    {op.opcLevel && <span className={`text-[8px] font-black px-2 py-0.5 rounded-full uppercase ${colors.badge}`}>{op.opcLevel.replace('_', ' ')}</span>}
                    {videoStatus === 'live' && <span className="flex items-center gap-1 text-[8px] font-black text-red-400 bg-red-500/10 px-2 py-0.5 rounded-full"><span className="w-1 h-1 rounded-full bg-red-500 animate-pulse" /> LIVE</span>}
                </div>
            </div>

            {/* Video area */}
            <div className="relative aspect-video bg-black">
                {videoEnabled ? (
                    <>
                        <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-contain" />
                        {videoStatus === 'connecting' && (
                            <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center gap-2">
                                <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{t('live.connectingVideo')}</span>
                            </div>
                        )}
                        {videoStatus === 'offline' && (
                            <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center gap-2">
                                <WifiOff className="w-6 h-6 text-gray-600" />
                                <span className="text-[9px] font-bold text-gray-500 uppercase tracking-widest">{t('live.noStream')}</span>
                            </div>
                        )}
                        {videoStatus === 'live' && (
                            <div className="absolute top-2 left-2 flex items-center gap-1.5 px-2 py-1 bg-red-600/90 rounded-md">
                                <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                                <span className="text-[8px] font-black text-white uppercase tracking-widest">{op.code}</span>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                        <CameraOff className="w-8 h-8 text-gray-700" />
                        <span className="text-[9px] font-bold text-gray-600 uppercase tracking-widest">{t('live.videoOff')}</span>
                    </div>
                )}
            </div>

            {/* Toggle controls */}
            <div className="p-3 space-y-2">
                <p className="text-[10px] text-gray-400 line-clamp-1">{op.description}</p>
                <div className="flex gap-2">
                    <button onClick={toggleVideo}
                        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all border ${videoEnabled
                            ? 'bg-red-500/15 border-red-500/30 text-red-400 hover:bg-red-500/25'
                            : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20'}`}>
                        {videoEnabled ? <><CameraOff className="w-3 h-3" /> {t('live.turnOff')}</> : <><Camera className="w-3 h-3" /> {t('live.video')}</>}
                    </button>
                    <button onClick={toggleVoice}
                        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all border ${voiceEnabled
                            ? 'bg-red-500/15 border-red-500/30 text-red-400 hover:bg-red-500/25'
                            : 'bg-indigo-500/10 border-indigo-500/20 text-indigo-400 hover:bg-indigo-500/20'}`}>
                        {voiceEnabled ? <><MicOff className="w-3 h-3" /> {t('live.mute')}</> : <><Radio className="w-3 h-3" /> {t('live.voice')}</>}
                    </button>
                    <button onClick={onJoin}
                        className="flex items-center justify-center gap-1.5 px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-[9px] font-black text-gray-400 uppercase tracking-widest hover:text-white hover:border-white/20 transition-all">
                        <Maximize2 className="w-3 h-3" />
                    </button>
                </div>
                {voiceEnabled && (
                    <div className="relative h-[180px] rounded-xl overflow-hidden border border-indigo-500/10 animate-in fade-in duration-300">
                        <iframe src={voiceUrl} className="w-full h-full border-none bg-[#1a1b26]" allow="microphone" title={t('live.voiceTitle', { code: op.code })} />
                    </div>
                )}
            </div>
        </div>
    );
}

export default function LiveStreamPage() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const isCommandRole = user?.roles?.some((r: string) => ['ADMINISTRADOR', 'COORDINADOR', 'SUPERVISOR'].includes(r));

    const [view, setView] = useState<PageView>('select');
    const [operations, setOperations] = useState<ActiveOperation[]>([]);
    const [selectedOp, setSelectedOp] = useState<ActiveOperation | null>(null);
    const [loadingOps, setLoadingOps] = useState(true);

    // Stream state
    const [mode, setMode] = useState<StreamMode>('idle');
    const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
    const [showVoice, setShowVoice] = useState(false);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [cameraEnabled, setCameraEnabled] = useState(true);
    const [micEnabled, setMicEnabled] = useState(true);
    const [, setBitrate] = useState(0);
    const [errorMessage, setErrorMessage] = useState('');

    const videoRef = useRef<HTMLVideoElement>(null);
    const localVideoRef = useRef<HTMLVideoElement>(null);
    const pcRef = useRef<RTCPeerConnection | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Fetch operations assigned to this user (ADMIN sees all)
    useEffect(() => {
        const fetchOps = async () => {
            try {
                setLoadingOps(true);
                // Primary: get only MY assigned operations (backend filters by role)
                const res = await api.get('/api/v1/operations/my-assigned');
                setOperations(res.data || []);
            } catch {
                // Fallback: old endpoint
                try {
                    const res = await api.get('/api/v1/operations/active');
                    setOperations(res.data || []);
                } catch {
                    try {
                        const res = await api.get('/api/v1/operations?size=100');
                        const all = res.data?.content || [];
                        setOperations(all.filter((op: any) => op.status === 'ACTIVA' || op.status === 'PLANIFICADA'));
                    } catch { setOperations([]); }
                }
            } finally { setLoadingOps(false); }
        };
        fetchOps();
        const interval = setInterval(fetchOps, 30000);
        return () => clearInterval(interval);
    }, []);

    // Timer
    useEffect(() => {
        if (mode === 'broadcasting' && connectionStatus === 'connected') {
            timerRef.current = setInterval(() => setElapsedTime(t => t + 1), 1000);
        } else {
            if (timerRef.current) clearInterval(timerRef.current);
            if (mode === 'idle') setElapsedTime(0);
        }
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, [mode, connectionStatus]);

    // Bitrate monitor
    useEffect(() => {
        if (!pcRef.current || connectionStatus !== 'connected') return;
        const interval = setInterval(async () => {
            const stats = await pcRef.current?.getStats();
            stats?.forEach((report: any) => {
                if (report.type === 'outbound-rtp' && report.kind === 'video') {
                    setBitrate(Math.round((report.bytesSent * 8) / 1000));
                }
            });
        }, 2000);
        return () => clearInterval(interval);
    }, [connectionStatus]);

    const formatTime = (secs: number) => {
        const h = Math.floor(secs / 3600);
        const m = Math.floor((secs % 3600) / 60);
        const s = secs % 60;
        return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    const joinOperation = (op: ActiveOperation) => {
        setSelectedOp(op);
        setView('channel');
        setMode('idle');
        setConnectionStatus('disconnected');
        setShowVoice(false);
    };

    const leaveChannel = () => {
        stopBroadcast();
        stopViewing();
        setShowVoice(false);
        setSelectedOp(null);
        setView('select');
    };

    // ═══════════════════════════════════════════════════
    // BROADCAST (WHIP)
    // ═══════════════════════════════════════════════════
    const startBroadcast = async () => {
        if (!selectedOp) return;
        const urls = getStreamUrls(selectedOp.code);
        try {
            setMode('broadcasting'); setConnectionStatus('connecting'); setErrorMessage('');
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 15, max: 30 } },
                audio: true
            });
            streamRef.current = stream;
            if (localVideoRef.current) localVideoRef.current.srcObject = stream;

            const pc = new RTCPeerConnection({ iceServers: await getIceServers() });
            pcRef.current = pc;
            stream.getTracks().forEach(track => pc.addTrack(track, stream));

            // Force H.264
            const vt = pc.getTransceivers().find(t => t.sender.track?.kind === 'video');
            if (vt) {
                try {
                    const caps = RTCRtpSender.getCapabilities?.('video');
                    if (caps) {
                        const h264 = caps.codecs.filter(c => c.mimeType === 'video/H264');
                        const other = caps.codecs.filter(c => c.mimeType !== 'video/H264');
                        if (h264.length > 0) vt.setCodecPreferences([...h264, ...other]);
                    }
                } catch { /* Firefox */ }
            }

            const offer = await pc.createOffer();
            let sdp = offer.sdp || '';
            const lines = sdp.split('\r\n');
            const vpPTs: string[] = [];
            lines.forEach(l => { const m = l.match(/^a=rtpmap:(\d+)\s+(VP8|VP9)\//i); if (m) vpPTs.push(m[1]); });
            if (vpPTs.length > 0) {
                sdp = lines.map(l => l.startsWith('m=video') ? l.split(' ').filter(p => !vpPTs.includes(p)).join(' ') : l)
                    .filter(l => !vpPTs.some(pt => l.match(new RegExp(`^a=(rtpmap|rtcp-fb|fmtp):${pt}\\b`)))).join('\r\n');
            }
            await pc.setLocalDescription({ type: 'offer', sdp });
            await new Promise<void>(r => { if (pc.iceGatheringState === 'complete') r(); else { pc.addEventListener('icegatheringstatechange', () => { if (pc.iceGatheringState === 'complete') r(); }); setTimeout(r, 5000); } });

            const res = await fetch(urls.whip, { method: 'POST', headers: { 'Content-Type': 'application/sdp' }, body: pc.localDescription!.sdp });
            if (!res.ok) throw new Error(`WHIP error: ${res.status}`);
            await pc.setRemoteDescription({ type: 'answer', sdp: await res.text() });
            pc.onconnectionstatechange = () => {
                if (pc.connectionState === 'connected') setConnectionStatus('connected');
                else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') { setConnectionStatus('error'); setErrorMessage(t('live.error.connectionLost')); }
            };
            setConnectionStatus('connected');
        } catch (err: any) {
            setConnectionStatus('error');
            setErrorMessage(err.name === 'NotAllowedError' ? t('live.error.cameraDenied') : err.name === 'NotFoundError' ? t('live.error.cameraNotFound') : err.message || t('live.error.broadcastFailed'));
        }
    };

    const stopBroadcast = () => {
        streamRef.current?.getTracks().forEach(t => t.stop()); streamRef.current = null;
        pcRef.current?.close(); pcRef.current = null;
        if (localVideoRef.current) localVideoRef.current.srcObject = null;
        if (videoRef.current) videoRef.current.srcObject = null;
        setMode('idle'); setConnectionStatus('disconnected'); setBitrate(0);
    };

    const toggleCamera = () => { const t = streamRef.current?.getVideoTracks()[0]; if (t) { t.enabled = !t.enabled; setCameraEnabled(t.enabled); } };
    const toggleMic = () => { const t = streamRef.current?.getAudioTracks()[0]; if (t) { t.enabled = !t.enabled; setMicEnabled(t.enabled); } };

    // ═══════════════════════════════════════════════════
    // VIEW (WHEP → HLS fallback)
    // ═══════════════════════════════════════════════════
    const startViewing = async () => {
        if (!selectedOp) return;
        const urls = getStreamUrls(selectedOp.code);
        setMode('viewing'); setConnectionStatus('connecting'); setErrorMessage('');
        try {
            const pc = new RTCPeerConnection({ iceServers: await getIceServers() });
            pcRef.current = pc;
            pc.addTransceiver('video', { direction: 'recvonly' });
            pc.addTransceiver('audio', { direction: 'recvonly' });
            pc.ontrack = (e) => { if (videoRef.current && e.streams[0]) videoRef.current.srcObject = e.streams[0]; };
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            await new Promise<void>(r => { if (pc.iceGatheringState === 'complete') r(); else { pc.addEventListener('icegatheringstatechange', () => { if (pc.iceGatheringState === 'complete') r(); }); setTimeout(r, 3000); } });
            const res = await fetch(urls.whep, { method: 'POST', headers: { 'Content-Type': 'application/sdp' }, body: pc.localDescription!.sdp });
            if (!res.ok) throw new Error(`WHEP error: ${res.status}`);
            await pc.setRemoteDescription({ type: 'answer', sdp: await res.text() });
            await new Promise<void>((resolve, reject) => {
                const t = setTimeout(() => reject(new Error('WHEP timeout')), 8000);
                pc.onconnectionstatechange = () => { if (pc.connectionState === 'connected') { clearTimeout(t); resolve(); } else if (pc.connectionState === 'failed') { clearTimeout(t); reject(new Error('WHEP failed')); } };
            });
            setConnectionStatus('connected');
        } catch {
            pcRef.current?.close(); pcRef.current = null;
            if (videoRef.current) videoRef.current.srcObject = null;
            // HLS fallback
            try {
                const Hls = (await import('hls.js')).default;
                if (!videoRef.current) return;
                if (Hls.isSupported()) {
                    const hls = new Hls({ enableWorker: true, lowLatencyMode: true, liveSyncDurationCount: 2, liveMaxLatencyDurationCount: 4, maxBufferLength: 4, maxMaxBufferLength: 8, backBufferLength: 0, liveBackBufferLength: 0, maxLiveSyncPlaybackRate: 1.5 });
                    hls.loadSource(urls.hls); hls.attachMedia(videoRef.current);
                    hls.on(Hls.Events.MANIFEST_PARSED, () => { videoRef.current?.play(); setConnectionStatus('connected'); });
                    hls.on(Hls.Events.ERROR, (_: any, d: any) => { if (d.fatal) { setConnectionStatus('error'); setErrorMessage(t('live.error.noActiveStream')); } });
                } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
                    videoRef.current.src = urls.hls;
                    videoRef.current.addEventListener('loadedmetadata', () => { videoRef.current?.play(); setConnectionStatus('connected'); });
                }
            } catch { setConnectionStatus('error'); setErrorMessage(t('live.error.noActiveStreamChannel')); }
        }
    };

    const stopViewing = () => {
        pcRef.current?.close(); pcRef.current = null;
        if (videoRef.current) { videoRef.current.srcObject = null; videoRef.current.src = ''; }
        setMode('idle'); setConnectionStatus('disconnected');
    };

    const toggleFullscreen = () => {
        if (!containerRef.current) return;
        if (!document.fullscreenElement) { containerRef.current.requestFullscreen(); setIsFullscreen(true); } else { document.exitFullscreen(); setIsFullscreen(false); }
    };

    const getOpcColor = (level?: string) => {
        if (level === 'NIVEL_III') return { bg: 'from-red-600/20 to-red-900/10', border: 'border-red-500/30', badge: 'bg-red-500/20 text-red-400', dot: 'bg-red-500' };
        if (level === 'NIVEL_II') return { bg: 'from-amber-600/20 to-amber-900/10', border: 'border-amber-500/30', badge: 'bg-amber-500/20 text-amber-400', dot: 'bg-amber-500' };
        return { bg: 'from-emerald-600/20 to-emerald-900/10', border: 'border-emerald-500/30', badge: 'bg-emerald-500/20 text-emerald-400', dot: 'bg-emerald-500' };
    };

    // ═══════════════════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════════════════
    return (
        <div className="h-full flex flex-col gap-5 md:gap-6" ref={containerRef}>
            {/* HEADER */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-lg sm:text-xl font-black text-white uppercase tracking-wider flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-800 flex items-center justify-center shadow-lg shadow-emerald-600/20">
                            <Signal className="w-5 h-5 text-white" />
                        </div>
                        {t('live.title')}
                    </h1>
                    <p className="text-[10px] sm:text-xs text-gray-500 mt-1 font-bold uppercase tracking-widest">
                        {selectedOp ? t('live.channelLabel', { code: selectedOp.code }) : t('live.selectOperationPrompt')}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    {selectedOp && (
                        <button onClick={leaveChannel} className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 border border-white/10 rounded-lg text-[9px] font-black text-gray-400 uppercase tracking-widest hover:text-white hover:border-white/20 transition-all">
                            <Layers className="w-3 h-3" /> {t('live.changeChannel')}
                        </button>
                    )}
                    {isCommandRole && view !== 'monitor' && (
                        <button onClick={() => { leaveChannel(); setView('monitor'); }} className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600/10 border border-indigo-500/20 rounded-lg text-[9px] font-black text-indigo-400 uppercase tracking-widest hover:bg-indigo-600/20 transition-all">
                            <Headphones className="w-3 h-3" /> {t('live.commandCenter')}
                        </button>
                    )}
                    {view === 'monitor' && (
                        <button onClick={() => setView('select')} className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 border border-white/10 rounded-lg text-[9px] font-black text-gray-400 uppercase tracking-widest hover:text-white transition-all">
                            <X className="w-3 h-3" /> {t('live.exitMonitor')}
                        </button>
                    )}
                    {mode !== 'idle' && connectionStatus === 'connected' && (
                        <div className="flex items-center gap-3 px-4 py-2 bg-black/40 rounded-xl border border-white/5">
                            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse shadow-[0_0_8px_#22c55e]" />
                            <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">{t('live.live')}</span>
                            {mode === 'broadcasting' && (
                                <span className="text-[10px] font-mono text-red-400 bg-red-500/10 px-2 py-0.5 rounded">● REC {formatTime(elapsedTime)}</span>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* ═══════════════════════════════════════════════════ */}
            {/* VIEW: OPERATION SELECTOR */}
            {/* ═══════════════════════════════════════════════════ */}
            {view === 'select' && (
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-4">
                        <Layers className="w-4 h-4 text-indigo-400" />
                        <h2 className="text-xs font-black text-white uppercase tracking-wider">{t('live.activeOperationsSelect')}</h2>
                        <span className="text-[9px] font-bold text-gray-600 bg-gray-800 px-2 py-0.5 rounded">{operations.length}</span>
                    </div>

                    {loadingOps ? (
                        <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
                    ) : operations.length === 0 ? (
                        <div className="flex flex-col items-center justify-center gap-4 py-20 bg-gray-900/30 rounded-2xl border border-white/5">
                            <AlertTriangle className="w-10 h-10 text-gray-600" />
                            <p className="text-xs text-gray-500 font-bold uppercase tracking-widest">{t('live.noActiveOperations')}</p>
                            <p className="text-[10px] text-gray-600">{t('live.noActiveOperationsHint')}</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {operations.map(op => {
                                const colors = getOpcColor(op.opcLevel);
                                return (
                                    <button key={op.id} onClick={() => joinOperation(op)}
                                        className={`group relative overflow-hidden bg-gradient-to-br ${colors.bg} border ${colors.border} rounded-2xl p-5 text-left transition-all duration-500 hover:scale-[1.02] hover:shadow-xl`}>
                                        <div className="relative z-10">
                                            <div className="flex items-center justify-between mb-3">
                                                <span className="text-[10px] font-black text-white bg-white/10 px-2 py-0.5 rounded-lg uppercase tracking-wider">{op.code}</span>
                                                {op.opcLevel && <span className={`text-[8px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest ${colors.badge}`}>{op.opcLevel.replace('_', ' ')}</span>}
                                            </div>
                                            <p className="text-xs text-gray-300 mb-3 line-clamp-2">{op.description}</p>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <span className={`w-1.5 h-1.5 rounded-full ${colors.dot} animate-pulse`} />
                                                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{op.status}</span>
                                                </div>
                                                <div className="flex items-center gap-1 text-gray-500 group-hover:text-white transition-colors">
                                                    <span className="text-[9px] font-black uppercase tracking-widest">{t('live.join')}</span>
                                                    <ChevronRight className="w-3 h-3" />
                                                </div>
                                            </div>
                                            {op.zone && <p className="text-[9px] text-gray-500 mt-2">📍 {op.zone}</p>}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* ═══════════════════════════════════════════════════ */}
            {/* VIEW: CHANNEL (Video + Voice for selected operation) */}
            {/* ═══════════════════════════════════════════════════ */}
            {view === 'channel' && selectedOp && (
                <div className="grid grid-cols-1 xl:grid-cols-[1fr,auto] gap-5">
                    {/* VIDEO MODULE */}
                    <div className="flex flex-col gap-0 rounded-2xl overflow-hidden border border-red-500/10 bg-gray-900/30">
                        <div className="flex items-center justify-between px-4 sm:px-5 py-3 bg-gradient-to-r from-red-950/60 via-red-900/20 to-transparent border-b border-red-500/10">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-xl bg-red-600/15 border border-red-500/20 flex items-center justify-center"><Camera className="w-4 h-4 text-red-400" /></div>
                                <div>
                                    <h2 className="text-xs font-black text-white uppercase tracking-wider">{t('live.videoChannel', { code: selectedOp.code })}</h2>
                                    <p className="text-[9px] text-gray-500 font-medium">{selectedOp.description}</p>
                                </div>
                            </div>
                            {mode !== 'idle' && connectionStatus === 'connected' && (
                                <div className="flex items-center gap-2 px-2.5 py-1 bg-red-600/10 border border-red-500/20 rounded-lg">
                                    <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse shadow-[0_0_6px_#ef4444]" />
                                    <span className="text-[9px] font-black text-red-400 uppercase tracking-widest">{mode === 'broadcasting' ? t('live.broadcasting') : t('live.receiving')}</span>
                                </div>
                            )}
                        </div>
                        <div className="flex-1 p-4 sm:p-5">
                            {/* IDLE — Action buttons */}
                            {mode === 'idle' && (
                                <div className="flex flex-col items-center justify-center gap-5 py-4 sm:py-6">
                                    <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/5 border border-green-500/20 rounded-full">
                                        <Shield className="w-3 h-3 text-green-500" /><span className="text-[9px] font-black text-green-500 uppercase tracking-widest">{t('live.secureConnection', { code: selectedOp.code })}</span>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-xl">
                                        <button onClick={startBroadcast} className="group relative overflow-hidden bg-gray-900/50 border border-white/5 hover:border-red-500/30 rounded-2xl p-5 sm:p-6 text-left transition-all duration-500 hover:shadow-[0_0_30px_rgba(239,68,68,0.1)]">
                                            <div className="absolute inset-0 bg-gradient-to-br from-red-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                            <div className="relative z-10">
                                                <div className="w-12 h-12 rounded-2xl bg-red-600/10 border border-red-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-500"><Camera className="w-6 h-6 text-red-500" /></div>
                                                <h3 className="text-sm font-black text-white uppercase tracking-wider mb-1.5">{t('live.broadcast')}</h3>
                                                <p className="text-[10px] text-gray-500 leading-relaxed">{t('live.broadcastDesc', { code: selectedOp.code })}</p>
                                                <div className="mt-3 flex items-center gap-2"><Phone className="w-3 h-3 text-gray-600" /><span className="text-[9px] font-bold text-gray-600 uppercase tracking-widest">{t('live.optimizedMobile')}</span></div>
                                            </div>
                                        </button>
                                        <button onClick={startViewing} className="group relative overflow-hidden bg-gray-900/50 border border-white/5 hover:border-blue-500/30 rounded-2xl p-5 sm:p-6 text-left transition-all duration-500 hover:shadow-[0_0_30px_rgba(59,130,246,0.1)]">
                                            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                            <div className="relative z-10">
                                                <div className="w-12 h-12 rounded-2xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-500"><Eye className="w-6 h-6 text-blue-500" /></div>
                                                <h3 className="text-sm font-black text-white uppercase tracking-wider mb-1.5">{t('live.viewStream')}</h3>
                                                <p className="text-[10px] text-gray-500 leading-relaxed">{t('live.viewStreamDesc', { code: selectedOp.code })}</p>
                                                <div className="mt-3 flex items-center gap-2"><Monitor className="w-3 h-3 text-gray-600" /><span className="text-[9px] font-bold text-gray-600 uppercase tracking-widest">{t('live.optimizedDesktop')}</span></div>
                                            </div>
                                        </button>
                                    </div>
                                </div>
                            )}
                            {/* BROADCASTING */}
                            {mode === 'broadcasting' && (
                                <div className="flex flex-col gap-4">
                                    <div className="relative bg-black rounded-2xl overflow-hidden border border-red-500/10 aspect-video w-full">
                                        <video ref={localVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
                                        {connectionStatus === 'connecting' && (<div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center gap-4"><div className="w-12 h-12 border-3 border-red-500 border-t-transparent rounded-full animate-spin" /><span className="text-xs font-black text-white uppercase tracking-widest">{t('live.establishingLink')}</span></div>)}
                                        {connectionStatus === 'error' && (<div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center gap-4 p-4"><AlertTriangle className="w-12 h-12 text-red-500" /><span className="text-xs font-black text-red-400 uppercase tracking-widest text-center">{errorMessage}</span><button onClick={() => { stopBroadcast(); startBroadcast(); }} className="flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/30 rounded-xl text-xs font-bold text-red-400 hover:bg-red-500/20 transition-colors"><RefreshCw className="w-3 h-3" /> {t('live.retry')}</button></div>)}
                                        {connectionStatus === 'connected' && (<div className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1.5 bg-red-600 rounded-lg shadow-lg"><div className="w-2 h-2 bg-white rounded-full animate-pulse" /><span className="text-[10px] font-black text-white uppercase tracking-widest">{t('live.liveChannel', { code: selectedOp.code })}</span></div>)}
                                        <div className="absolute bottom-3 left-3 flex items-center gap-2 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-lg"><Shield className="w-3 h-3 text-blue-400" /><span className="text-[10px] font-bold text-white">{user?.fullName || t('live.operator')}</span></div>
                                    </div>
                                    <div className="flex items-center justify-center gap-3 py-1">
                                        <button onClick={toggleCamera} className={`p-3 rounded-2xl transition-all ${cameraEnabled ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>{cameraEnabled ? <Camera className="w-5 h-5" /> : <CameraOff className="w-5 h-5" />}</button>
                                        <button onClick={toggleMic} className={`p-3 rounded-2xl transition-all ${micEnabled ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>{micEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}</button>
                                        <button onClick={stopBroadcast} className="px-5 py-3 bg-red-600 hover:bg-red-500 text-white rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-lg shadow-red-600/20 flex items-center gap-2"><VideoOff className="w-4 h-4" /><span className="hidden sm:inline">{t('live.stop')}</span></button>
                                        <button onClick={toggleFullscreen} className="p-3 rounded-2xl bg-white/10 text-white hover:bg-white/20 transition-all">{isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}</button>
                                    </div>
                                </div>
                            )}
                            {/* VIEWING */}
                            {mode === 'viewing' && (
                                <div className="flex flex-col gap-4">
                                    <div className="relative bg-black rounded-2xl overflow-hidden border border-blue-500/10 aspect-video w-full">
                                        <video ref={videoRef} autoPlay playsInline className="w-full h-full object-contain" />
                                        {connectionStatus === 'connecting' && (<div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center gap-4"><div className="w-12 h-12 border-3 border-blue-500 border-t-transparent rounded-full animate-spin" /><span className="text-xs font-black text-white uppercase tracking-widest">{t('live.searchingStream')}</span></div>)}
                                        {connectionStatus === 'error' && (<div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center gap-5 p-4"><div className="w-16 h-16 rounded-full bg-gray-800/50 flex items-center justify-center"><WifiOff className="w-8 h-8 text-gray-600" /></div><div className="text-center"><h3 className="text-sm font-black text-white uppercase tracking-wider mb-2">{t('live.noStreamTitle')}</h3><p className="text-xs text-gray-500 max-w-xs">{errorMessage || t('live.noOperatorsBroadcasting', { code: selectedOp.code })}</p></div><button onClick={() => { stopViewing(); startViewing(); }} className="flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-xl text-xs font-bold text-blue-400 hover:bg-blue-500/20 transition-colors"><RefreshCw className="w-3 h-3" /> {t('live.retry')}</button></div>)}
                                        {connectionStatus === 'connected' && (<div className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1.5 bg-blue-600 rounded-lg shadow-lg"><Eye className="w-3 h-3 text-white" /><span className="text-[10px] font-black text-white uppercase tracking-widest">{t('live.monitoringChannel', { code: selectedOp.code })}</span></div>)}
                                    </div>
                                    <div className="flex items-center justify-center gap-3 py-1">
                                        <button onClick={toggleFullscreen} className="p-3 rounded-2xl bg-white/10 text-white hover:bg-white/20 transition-all">{isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}</button>
                                        <button onClick={stopViewing} className="px-5 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest transition-all flex items-center gap-2"><X className="w-4 h-4" /> {t('live.close')}</button>
                                        <button onClick={() => { stopViewing(); startViewing(); }} className="p-3 rounded-2xl bg-white/10 text-white hover:bg-white/20 transition-all"><RefreshCw className="w-5 h-5" /></button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* VOICE PTT MODULE */}
                    <div className="flex flex-col gap-0 rounded-2xl overflow-hidden border border-indigo-500/10 bg-gray-900/30 xl:w-[420px]">
                        <div className="flex items-center justify-between px-4 sm:px-5 py-3 bg-gradient-to-r from-indigo-950/60 via-indigo-900/20 to-transparent border-b border-indigo-500/10">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-xl bg-indigo-600/15 border border-indigo-500/20 flex items-center justify-center"><Radio className="w-4 h-4 text-indigo-400 animate-pulse" /></div>
                                <div>
                                    <h2 className="text-xs font-black text-white uppercase tracking-wider">{t('live.radioPtt', { code: selectedOp.code })}</h2>
                                    <p className="text-[9px] text-gray-500 font-medium">{t('live.pttSubtitle')}</p>
                                </div>
                            </div>
                            <button onClick={() => setShowVoice(!showVoice)}
                                className={`text-[9px] font-black uppercase tracking-widest px-3 py-1.5 rounded-lg border transition-all ${showVoice ? 'bg-red-500/15 border-red-500/30 text-red-400 hover:bg-red-500/25' : 'bg-white/5 border-white/10 text-gray-400 hover:text-white hover:border-white/20'}`}>
                                {showVoice ? t('live.disconnect') : t('live.connect')}
                            </button>
                        </div>
                        <div className="flex-1">
                            {showVoice ? (
                                <div className="relative h-[450px] xl:h-[500px]">
                                    <iframe src={getVoiceUrl(selectedOp.code, user?.username || 'Operador')} className="w-full h-full border-none bg-[#1a1b26]" allow="microphone" title={t('live.mumbleTitle', { code: selectedOp.code })} />
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center gap-4 py-12 sm:py-16 px-6">
                                    <div className="w-16 h-16 rounded-2xl bg-indigo-600/10 border border-indigo-500/15 flex items-center justify-center"><Mic className="w-8 h-8 text-indigo-500/40" /></div>
                                    <div className="text-center">
                                        <h3 className="text-xs font-black text-gray-400 uppercase tracking-wider mb-1">{t('live.radioDisconnected')}</h3>
                                        <p className="text-[10px] text-gray-600 leading-relaxed max-w-[200px]">{t('live.radioDisconnectedHint', { code: selectedOp.code })}</p>
                                    </div>
                                    <button onClick={() => setShowVoice(true)} className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600/10 border border-indigo-500/20 rounded-xl text-xs font-black text-indigo-400 uppercase tracking-widest hover:bg-indigo-600/20 hover:border-indigo-500/30 transition-all">
                                        <Radio className="w-3.5 h-3.5" /> {t('live.connectRadio')}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ═══════════════════════════════════════════════════ */}
            {/* VIEW: COMMAND CENTER (Multi-channel monitoring) */}
            {/* ═══════════════════════════════════════════════════ */}
            {view === 'monitor' && (
                <div className="flex-1">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="w-8 h-8 rounded-xl bg-indigo-600/15 border border-indigo-500/20 flex items-center justify-center"><Headphones className="w-4 h-4 text-indigo-400" /></div>
                        <div>
                            <h2 className="text-xs font-black text-white uppercase tracking-wider">{t('live.commandCenterTitle')}</h2>
                            <p className="text-[9px] text-gray-500 font-medium">{t('live.commandCenterSubtitle')}</p>
                        </div>
                        <span className="text-[9px] font-bold text-indigo-400 bg-indigo-500/10 px-2 py-0.5 rounded">{t('live.channelsCount', { count: operations.length })}</span>
                    </div>
                    {operations.length === 0 ? (
                        <div className="flex flex-col items-center justify-center gap-4 py-20 bg-gray-900/30 rounded-2xl border border-white/5">
                            <Users className="w-10 h-10 text-gray-600" />
                            <p className="text-xs text-gray-500 font-bold uppercase tracking-widest">{t('live.noOperationsToMonitor')}</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {operations.map(op => {
                                const colors = getOpcColor(op.opcLevel);
                                const voiceUrl = getVoiceUrl(op.code, `CMD-${user?.username || 'Admin'}`);
                                return <MonitorCard key={op.id} op={op} colors={colors} voiceUrl={voiceUrl} onJoin={() => joinOperation(op)} />;
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
